import { LayoutDashboard, FileText, Zap, Settings, LogOut, Activity, Heart, BookOpen, Trophy } from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Logo } from '@/components/Logo';
import { CURRENT_VERSION } from '@/lib/version';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  useSidebar,
} from '@/components/ui/sidebar';

const managerItems = [
  { title: 'Dashboard', url: '/dashboard', icon: LayoutDashboard },
  { title: 'View Deals', url: '/deals', icon: FileText },
  { title: 'Demand Board', url: '/wishlist', icon: Heart },
  { title: 'Programs', url: '/programs', icon: BookOpen },
  { title: 'Leaderboard', url: '/leaderboard', icon: Trophy },
  { title: 'Settings', url: '/settings', icon: Settings },
];

const healthItem = { title: 'Pilot Health', url: '/dashboard/health', icon: Activity };

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, mode, isSolo, role } = useAuth();
  const isManager = role === 'manager' || role === 'admin';
  const showHealth = mode === 'real' && !isSolo && isManager;
  const repItems = [
    { title: 'Home', url: '/rep', icon: LayoutDashboard },
    { title: 'My Deals', url: '/rep', icon: FileText },
    { title: 'Programs', url: '/programs', icon: BookOpen },
    { title: 'Demand Board', url: '/wishlist', icon: Heart },
    { title: 'Quick Log', url: '/log-activity', icon: Zap },
    { title: 'Settings', url: '/settings', icon: Settings },
  ];
  const items = !isManager || isSolo
    ? repItems
    : showHealth
      ? [managerItems[0], healthItem, ...managerItems.slice(1)]
      : managerItems;

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <Sidebar collapsible="icon">
      <div className="flex items-center justify-center px-3 py-3 border-b border-sidebar-border">
        <Link to="/" aria-label="PullOver.App home" className="flex items-center justify-center w-full">
          {collapsed ? (
            <Logo variant="square" className="h-8 w-8 object-contain" />
          ) : (
            <Logo variant="full" className="w-full max-h-20 object-contain" />
          )}
        </Link>
      </div>
      <SidebarContent>
        {isManager && !isSolo && !collapsed && (
          <div className="px-2 pt-3">
            <Link
              to="/log-activity"
              className="flex items-center justify-center gap-2 w-full h-10 rounded-md bg-primary text-primary-foreground font-semibold text-sm shadow-[0_0_24px_-8px_hsl(var(--primary)/0.7)] hover:brightness-110 transition"
            >
              <Zap className="h-4 w-4" /> Quick Log
            </Link>
          </div>
        )}
        {isManager && !isSolo && collapsed && (
          <div className="px-1 pt-3 flex justify-center">
            <Link
              to="/log-activity"
              aria-label="Quick Log"
              className="flex items-center justify-center w-9 h-9 rounded-md bg-primary text-primary-foreground shadow-[0_0_18px_-6px_hsl(var(--primary)/0.7)] hover:brightness-110 transition"
            >
              <Zap className="h-4 w-4" />
            </Link>
          </div>
        )}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {items.map(item => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={item.url}
                      end={item.url === '/dashboard'}
                      className="hover:bg-sidebar-accent/50"
                      activeClassName="bg-sidebar-accent text-sidebar-primary font-medium"
                    >
                      <item.icon className="mr-2 h-4 w-4" />
                      {!collapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="border-t border-sidebar-border p-2">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={handleLogout} className="text-muted-foreground hover:text-foreground">
              <LogOut className="mr-2 h-4 w-4" />
              {!collapsed && (
                <span className="flex items-center gap-2">
                  Sign Out
                  <span className="text-xs text-muted-foreground/70">| {CURRENT_VERSION}</span>
                </span>
              )}
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
